import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  MagnifyingGlass,
  Plus,
  DotsThreeVertical,
  Power,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import api, { cleanPayload, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const EMPTY_FORM = {
  school_name: "",
  address: "",
  phone: "",
  email: "",
  upi_id: "",
  receipt_footer: "",
  theme_color: "#1E3A8A",
  admin_email: "",
  admin_name: "",
  admin_password: "",
  admin_phone: "",
};

function CreateSchoolDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setError("");
    }
  }, [open]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/super-admin/schools", cleanPayload(form));
      toast.success(`${data.school_name} onboarded`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="create-school-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Onboard a new school</DialogTitle>
          <DialogDescription>
            Creates a school tenant and its initial school admin account.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">
              School details
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>School name *</Label>
            <Input
              required
              data-testid="create-school-name"
              value={form.school_name}
              onChange={update("school_name")}
              className="mt-1.5"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Address</Label>
            <Input value={form.address} onChange={update("address")} className="mt-1.5" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={update("phone")} className="mt-1.5" />
          </div>
          <div>
            <Label>Contact email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={update("email")}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>UPI ID</Label>
            <Input
              value={form.upi_id}
              onChange={update("upi_id")}
              placeholder="school@upi"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>Theme color</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                type="color"
                value={form.theme_color}
                onChange={update("theme_color")}
                className="h-10 w-14 rounded border border-slate-200"
              />
              <Input value={form.theme_color} onChange={update("theme_color")} />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Receipt footer</Label>
            <Input
              value={form.receipt_footer}
              onChange={update("receipt_footer")}
              placeholder="Thank you for your payment"
              className="mt-1.5"
            />
          </div>

          <div className="md:col-span-2 mt-2 pt-3 border-t border-slate-200">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">
              Initial school admin
            </div>
          </div>
          <div>
            <Label>Admin name *</Label>
            <Input
              required
              data-testid="create-admin-name"
              value={form.admin_name}
              onChange={update("admin_name")}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>Admin phone</Label>
            <Input value={form.admin_phone} onChange={update("admin_phone")} className="mt-1.5" />
          </div>
          <div>
            <Label>Admin email *</Label>
            <Input
              required
              type="email"
              data-testid="create-admin-email"
              value={form.admin_email}
              onChange={update("admin_email")}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>Admin password *</Label>
            <Input
              required
              type="text"
              minLength={8}
              data-testid="create-admin-password"
              value={form.admin_password}
              onChange={update("admin_password")}
              className="mt-1.5 font-mono"
              placeholder="Min 8 characters"
            />
          </div>

          {error && (
            <div className="md:col-span-2 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </div>
          )}

          <DialogFooter className="md:col-span-2 mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="create-school-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="create-school-submit"
              className="bg-blue-900 hover:bg-blue-800"
            >
              {submitting ? "Creating…" : "Create school"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function SchoolsList() {
  const [params, setParams] = useSearchParams();
  const [schools, setSchools] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [openCreate, setOpenCreate] = useState(params.get("action") === "create");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const url = q ? `/super-admin/schools?search=${encodeURIComponent(q)}` : "/super-admin/schools";
      const { data } = await api.get(url);
      setSchools(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load("");
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const toggleStatus = async (school) => {
    try {
      const { data } = await api.post(`/super-admin/schools/${school.id}/toggle-status`);
      setSchools((s) => s.map((x) => (x.id === school.id ? data : x)));
      toast.success(`${data.school_name} is now ${data.status}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="schools-list-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
            Schools
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
            All schools <span className="text-slate-400 font-normal">({total})</span>
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Onboard new schools and manage tenant lifecycle.
          </p>
        </div>
        <Button
          onClick={() => {
            setOpenCreate(true);
            setParams({ action: "create" });
          }}
          data-testid="open-create-school-btn"
          className="bg-blue-900 hover:bg-blue-800 gap-2"
        >
          <Plus size={16} weight="bold" /> New school
        </Button>
      </div>

      <div className="border border-slate-200 bg-white rounded-lg">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search schools…"
              className="pl-9 h-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="schools-search-input"
            />
          </div>
        </div>
        {error && (
          <div className="px-4 py-3 text-sm text-red-600 bg-red-50 border-b border-red-200">{error}</div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="schools-table">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left font-semibold px-4 py-3">School</th>
                <th className="text-left font-semibold px-4 py-3">Contact</th>
                <th className="text-left font-semibold px-4 py-3">UPI</th>
                <th className="text-left font-semibold px-4 py-3">Status</th>
                <th className="text-right font-semibold px-4 py-3">Created</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && schools.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              ) : schools.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500" data-testid="schools-empty">
                    No schools yet. Click <strong>New school</strong> to onboard your first tenant.
                  </td>
                </tr>
              ) : (
                schools.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50 transition-colors" data-testid={`school-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="h-9 w-9 rounded flex items-center justify-center text-white text-sm font-bold font-heading"
                          style={{ backgroundColor: s.theme_color || "#1E3A8A" }}
                        >
                          {s.school_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">{s.school_name}</div>
                          <div className="text-xs text-slate-500 truncate">{s.address || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="text-sm">{s.email || "—"}</div>
                      <div className="text-xs text-slate-500">{s.phone || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 font-mono text-xs">{s.upi_id || "—"}</td>
                    <td className="px-4 py-3">
                      {s.status === "active" ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded bg-emerald-100 text-emerald-700">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded bg-slate-200 text-slate-700">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-slate-500">
                      {new Date(s.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                            data-testid={`school-actions-${s.id}`}
                          >
                            <DotsThreeVertical size={18} weight="bold" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/schools/${s.id}`} data-testid={`view-school-${s.id}`}>
                              <ArrowSquareOut size={14} className="mr-2" /> View details
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => toggleStatus(s)}
                            data-testid={`toggle-status-${s.id}`}
                          >
                            <Power size={14} className="mr-2" />
                            {s.status === "active" ? "Deactivate" : "Activate"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateSchoolDialog
        open={openCreate}
        onOpenChange={(v) => {
          setOpenCreate(v);
          if (!v) setParams({});
        }}
        onCreated={(school) => setSchools((s) => [school, ...s])}
      />
    </div>
  );
}
